import { useEffect, useMemo, useState } from 'react';
import { FiBell, FiSend } from 'react-icons/fi';
import Layout from '../components/Layout';
import api from '../api';

const emptyForm = {
  recipient: '',
  title: '',
  body: '',
  level: 'info',
  actionLabel: '',
  actionUrl: '',
};

export default function ContentControl() {
  const [notifications, setNotifications] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  async function loadNotifications() {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/platform/notifications');
      setNotifications(data.notifications || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load broadcast history');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSending(true);
    setError('');
    setMessage('');
    try {
      const { data } = await api.post('/admin/platform/notifications', form);
      setMessage(data.message || 'Notification queued.');
      setForm(emptyForm);
      window.dispatchEvent(new Event('admin-notifications:updated'));
      await loadNotifications();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not send this broadcast');
    } finally {
      setSending(false);
    }
  }

  const totals = useMemo(() => {
    const broadcastCount = notifications.filter(
      (notification) => notification?.metadata?.recipient === 'all-users'
    ).length;
    return {
      total: notifications.length,
      broadcastCount,
      directCount: notifications.length - broadcastCount,
    };
  }, [notifications]);

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top,rgba(245,178,33,0.2),transparent_32%),linear-gradient(180deg,#111111,#030303)] p-8 shadow-2xl">
          <p className="text-sm uppercase tracking-[0.24em] text-amber-200">Broadcast Center</p>
          <h1 className="mt-4 text-4xl font-black tracking-[-0.03em] text-white">
            Send suite-wide or targeted Shimbirr notifications from one place.
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">
            The rest of the old content tooling is removed here. This admin section now stays focused on customer communication across the Shimbirr suite.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Metric label="Recent total" value={String(totals.total)} />
            <Metric label="Broadcasts" value={String(totals.broadcastCount)} />
            <Metric label="Direct sends" value={String(totals.directCount)} />
          </div>
        </section>

        {message ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        <section className="grid gap-6 xl:grid-cols-[0.95fr,1.15fr]">
          <form
            onSubmit={handleSubmit}
            className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl"
          >
            <div className="flex items-center gap-3">
              <FiSend className="text-amber-200" />
              <div>
                <h2 className="text-2xl font-bold text-white">Send notification</h2>
                <p className="mt-2 text-sm text-slate-400">
                  Leave recipient empty to broadcast to all Shimbirr users, or target one email, Shimbirr ID, or user ID.
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <Field label="Recipient">
                <input
                  name="recipient"
                  value={form.recipient}
                  onChange={handleChange}
                  placeholder="all users if blank"
                  className={inputClass}
                />
              </Field>
              <Field label="Title">
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  className={inputClass}
                />
              </Field>
              <Field label="Body">
                <textarea
                  name="body"
                  value={form.body}
                  onChange={handleChange}
                  required
                  rows={5}
                  className={inputClass}
                />
              </Field>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Level">
                  <select name="level" value={form.level} onChange={handleChange} className={inputClass}>
                    <option value="info">Info</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                </Field>
                <Field label="Action label">
                  <input name="actionLabel" value={form.actionLabel} onChange={handleChange} className={inputClass} />
                </Field>
                <Field label="Action URL">
                  <input name="actionUrl" value={form.actionUrl} onChange={handleChange} className={inputClass} />
                </Field>
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-300 px-5 py-3 font-semibold text-slate-950 transition hover:opacity-90 disabled:opacity-60"
            >
              <FiSend /> {sending ? 'Sending...' : 'Queue notification'}
            </button>
          </form>

          <section className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <FiBell className="text-amber-300" />
              <div>
                <h2 className="text-2xl font-bold text-white">Recent sends</h2>
                <p className="mt-2 text-sm text-slate-400">
                  Last notifications sent through the Shimbirr admin broadcast center.
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {loading ? (
                <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-slate-400">
                  Loading broadcast history...
                </div>
              ) : null}

              {!loading && !notifications.length ? (
                <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-slate-400">
                  No notification history yet.
                </div>
              ) : null}

              {!loading
                ? notifications.map((notification) => (
                    <div key={notification._id} className="rounded-3xl border border-white/5 bg-black/35 p-5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-semibold text-white">{notification.title}</p>
                          <p className="mt-2 text-sm leading-6 text-slate-300">{notification.body}</p>
                        </div>
                        <LevelPill level={notification.level} />
                      </div>
                      <div className="mt-4 flex flex-wrap gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
                        <span>{notification?.metadata?.recipient || 'all-users'}</span>
                        <span>{formatDateTime(notification.createdAt)}</span>
                        <span>{notification.type || 'admin_broadcast'}</span>
                      </div>
                    </div>
                  ))
                : null}
            </div>
          </section>
        </section>
      </div>
    </Layout>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-3xl border border-white/5 bg-black/30 p-5">
      <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{label}</p>
      <p className="mt-2 text-3xl font-black tracking-[-0.03em] text-white">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-[0.22em] text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function LevelPill({ level }) {
  const tone = {
    info: 'border-sky-400/30 bg-sky-500/10 text-sky-100',
    success: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100',
    warning: 'border-amber-400/30 bg-amber-500/10 text-amber-100',
    critical: 'border-rose-500/30 bg-rose-500/10 text-rose-100',
  }[String(level || '').toLowerCase()] || 'border-slate-400/20 bg-slate-500/10 text-slate-100';

  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] ${tone}`}>
      {String(level || 'info')}
    </span>
  );
}

function formatDateTime(value) {
  if (!value) return 'Unknown time';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

const inputClass =
  'w-full rounded-2xl border border-gray-700 bg-black px-4 py-3 text-white outline-none transition focus:border-amber-300';
